/**
 * Wire live QR RAW recording + the WS-5 debug viz into a running AR session.
 *
 * Composes the two halves of the recorder live-QR feature, gated by the operator
 * (`recordingOptions.qr.enabled`):
 *  - **Producer (WS-2):** the framework's thin `createQrDetectionController` fed by
 *    the camera-frame source. On each accepted decode it dispatches a RAW
 *    `recordQrDetection` (corners + camera pose + projection + frame size +
 *    timestamp) into the CURRENT store, so the persistence middleware records it.
 *  - **Consumer (WS-5):** a `createQrDebugController` subscribed to the store
 *    (re-attaching across `Start Recording` / replay store swaps) that renders the
 *    derived axis+cube under `arWorldGroup`.
 *
 * **Clock domain (load-bearing — plan open topic A):** a detection's timestamp is
 * its frame's capture time, EPOCH ms (`performance.timeOrigin + xrTime`, stamped by
 * the framework since QR perf plan M4) - the SAME clock the recorded depth stream
 * uses (`DepthSample.timestamp = performance.timeOrigin + frameTs`,
 * depth-sampler.ts), so the derive-on-read size as-of join
 * (`depth.ts <= detection.ts`) pairs each detection with the right depth sample. Using `performance.now()` (relative)
 * here was a bug: it never satisfies the join, so the size — and the debug cube —
 * never resolve.
 *
 * Camera POSE comes WITH each captured frame (the framework pairs the pixels with
 * the pose of the XR frame they were captured in - QR perf plan 2026-09-23, M4),
 * so it is neither stale to the 1 Hz depth cadence nor read after the async
 * decode; PROJECTION (PnP intrinsics) still
 * comes from the latest depth sample (the only per-frame projection source today;
 * a fresher per-frame projection is open topic F). The producer's
 * `imageWidth/Height` come from the detector-frame buffer (the RGBA capture).
 *
 * The frame SOURCE is the single cadence owner (`startCameraFrameCapture({
 * intervalMs })`); the producer runs `minIntervalMs: 0` and detects every
 * delivered frame.
 *
 * @see qr-debug-controller.ts / qr-depth-resolver.ts — the consumer + as-of join.
 * @see gps-plus-slam-app-framework/ar/qr/qr-detection-controller — the thin producer.
 */

import type { Object3D } from 'three';
// Deep subpaths (NOT the `…/ar` barrel): the barrel eagerly pulls heavy
// transitive deps (e.g. sensors/permission-checker), which breaks main.ts's
// wiring tests that mock those partially. Same rationale as the demo's
// qr-debug-view. We import only the few modules we actually use.
import { createQrDetectionController } from 'gps-plus-slam-app-framework/ar/qr/qr-detection-controller';
import {
  createBarcodeDetectorFrontEnd,
  type RgbaImage,
} from 'gps-plus-slam-app-framework/ar/qr/qr-frontend';
import {
  startCameraFrameCapture,
  stopCameraFrameCapture,
} from 'gps-plus-slam-app-framework/ar/webxr-session';
import type { QrCaptureOptions } from '../state/recording-options';
import { recordQrDetection } from 'gps-plus-slam-app-framework/state';
import type { RecorderStore } from '../state/recorder-store';
import type { StoreRef } from '../state/store-ref';
import { followStore } from '../state/store-ref';
import { createQrTrackingController } from 'gps-plus-slam-app-framework/ar/qr/qr-tracking-controller';
import {
  intrinsicsFromProjection,
  solveQrPose,
} from 'gps-plus-slam-app-framework/ar/qr/qr-pose';
import { PlanarPnpSquare } from 'gps-plus-slam-app-framework/ar/qr/planar-pnp';
import { recordGpsEvent } from 'gps-plus-slam-app-framework/state';
import { createQrDebugController } from './qr-debug-controller';
import {
  createQrLevelSource,
  type QrLevelLookupState,
} from './qr-level-source';
import { QR_LAUNCH_HOSTS } from './qr-launch-hosts';
// Shared with the TourViewer rather than re-decided here (DEC-H3): the
// controller votes on every locked FRAME, so an unbudgeted code pins the
// alignment centroid to the poster.
import { createQrVoteBudget } from 'gps-plus-slam-app-framework/ar/qr/qr-vote-budget';
import type { QrFrameSink } from '../ar/ar-session-resources';
import {
  createQrSightingFeeder,
  type QrSightingFeeder,
  type QrSightingFeederDeps,
} from './qr-sighting-feeder';
import { createQrFusedVotes } from './qr-fused-votes';

/** Bare-name `?qr=` payloads resolve under this prefix — the convention the
 *  framework's launch builder documents. */
const DEFAULT_ASSET_PREFIX =
  'https://raw.githubusercontent.com/cs-util-com/GeoTales/refs/heads/main/';

/** The site worker's Drive CORS proxy: keyless Drive links refuse a real
 *  browser fetch, so they are rewritten through this route. */
const DRIVE_PROXY_BASE_URL = 'https://gps.csutil.com/api/drive-proxy';

/** Vote shape, carried over from the shipped viewer unchanged — the reasons
 *  those numbers are what they are live in the QR-pose plan's M4. */
const VOTE_ACCURACY_M = 5;
const VOTE_BASELINE_M = 2;
const VOTE_COUNT = 4;

export interface WireQrRecordingOptions {
  /** The active-store ref (producer + viz follow store swaps through it). */
  storeRef: StoreRef<RecorderStore>;
  /** The `arWorldGroup` the debug objects parent under (`null` until ready). */
  getArWorldGroup: () => Object3D | null;
  /** QR capture settings (`enabled` is assumed true by the caller's gate). */
  qr: QrCaptureOptions;
  /**
   * Receives the created producer so the camera-frame callback passed to
   * `initAR` (`callbacks.cameraFrame.onFrame`, wired before the source is
   * built) can forward frames to it. Called with `null` on dispose.
   */
  setProducer: (producer: QrFrameSink | null) => void;
  /**
   * Read the session's alignment as it stands NOW, with the session's GPS
   * extent. The feeder reads it at every detection and every store change,
   * because the store keeps no history: a code is minted through the first
   * alignment at or after its last sighting whose extent reaches 40 m (D34), else
   * through the alignment at save (owner decision D28, revised 2026-10-02).
   */
  readAlignment: QrSightingFeederDeps['readAlignment'];
  /** Receives the sighting feeder so save-time minting and the HUD can read
   *  it. Called with `null` on dispose. */
  setSightingFeeder?: (feeder: QrSightingFeeder | null) => void;
  /** What a scanned code's level lookup did, for the HUD. */
  onLevelState?: (text: string, state: QrLevelLookupState) => void;
  /**
   * QR store state changed (coalesced to one call per animation frame), for
   * the HUD row. Independent of camera frames, which the capture veto pauses
   * while a detect or level fetch is in flight.
   */
  onQrStateChanged?: () => void;
}

/**
 * Start QR recording + debug viz. Returns a dispose that stops capture, detaches
 * the producer, tears down the debug subscriber + views, and resets the resolver.
 */
export function wireQrRecording(options: WireQrRecordingOptions): () => void {
  const { storeRef, getArWorldGroup, qr, setProducer } = options;
  /** Set only in level-consuming mode. */
  let disposeLevelSource: (() => void) | null = null;
  /**
   * Whichever object is consuming camera frames this session - the tracking
   * controller in level mode, the thin producer otherwise. Teardown must reset
   * THIS, not the thin producer specifically: in level mode the thin producer
   * is never built, so resetting it was a no-op and the tracking controller's
   * `active` was never cleared. A detection already awaiting its level fetch
   * then came back after the session ended and dispatched into whatever store
   * was current by then.
   */
  let frameSink: { dispose: () => void } | null = null;
  /** True once the tracking controller has taken over the frame stream. */
  let usingLevels = false;
  /**
   * How many synthetic QR votes THIS session has dispatched into the current
   * store. Subtracted below, because the count the mint reads is meant to be
   * evidence of independent GPS support and a QR vote is not that.
   *
   * Reset on a store swap: the new store restarts its GPS list from scratch,
   * so votes dispatched into the previous one must stop being subtracted.
   */
  let syntheticVotes = 0;

  /**
   * Per-code vote budget. Without it `dispatchVotes` fires on EVERY locked
   * frame - roughly 8 Hz x 4 correspondences while a previously-authored
   * code is in view - and those synthetic points pin the alignment centroid
   * to the poster (PR #385 review). That matters more here than in the
   * viewer: this alignment is what `qr-anchor-mint` mints every OTHER code
   * in the session against, so the pinned centroid would propagate one
   * code's error into every new `qr/<id>.json`.
   */
  const voteBudget = createQrVoteBudget();
  // The votes ride the FUSED pose (QR near-frontal pose plan §71-§72, b6a;
  // the owner's call, §66): a code votes only once the joint rotation over
  // its recent detections is stable, at that rotation. Evaluated per
  // recorded detection, so a replay rebuilds the same gate.
  const fusedVotes = createQrFusedVotes({
    getQrState: () => storeRef.get().getState(),
    isSpent: (text) => voteBudget.isSpent(text),
  });
  /** The code the next vote batch belongs to; see `onDetection`. */
  let lastVotedText: string | null = null;

  const sightings = createQrSightingFeeder({
    // The stored-GPS count includes this session's own synthetic votes -
    // they go through the same `recordGpsEvent` the real fixes do. Left
    // unsubtracted, one lock on a previously authored code dispatches four
    // votes and clears `MIN_ALIGNMENT_SAMPLES` with zero real fixes, and the
    // published `qr/<id>.json` then claims GPS support that is really a
    // re-projection of an older code's anchor, error included.
    //
    // Subtracting a session counter from a per-store list can go past zero
    // (the list empties on a swap), hence the clamp.
    readAlignment: () => {
      const alignment = options.readAlignment();
      return {
        ...alignment,
        alignmentSampleCount: Math.max(
          0,
          alignment.alignmentSampleCount - syntheticVotes
        ),
      };
    },
  });
  options.setSightingFeeder?.(sightings);

  // --- Producer (WS-2) ------------------------------------------------------
  const frontEnd = createBarcodeDetectorFrontEnd();
  const detect = frontEnd
    ? (image: RgbaImage) => frontEnd.detect(image)
    : () => Promise.resolve(null);

  // Projection (PnP intrinsics) still comes from the depth sample — the only
  // per-frame projection source today, and FOV is near-constant per session
  // (a fresher per-frame projection is open topic F).
  const getProjectionMatrix = () =>
    storeRef.get().getState().recording.latestDepthSample?.projectionMatrix ??
    null;

  // Level-consuming mode (DEC-7): the SAME frames, but through the tracking
  // controller, which fetches the code's level and votes. The raw record is
  // still written — the widened detection event carries the corners and the
  // camera pose, so ONE decode feeds both. Running the thin producer beside
  // it would decode every frame twice on the AR frame path.
  if (qr.useLevels && frontEnd) {
    const pnpSolver = new PlanarPnpSquare();
    const levelSource = createQrLevelSource({
      allowedHosts: QR_LAUNCH_HOSTS,
      assetPrefix: DEFAULT_ASSET_PREFIX,
      corsProxyBaseUrl: DRIVE_PROXY_BASE_URL,
      onState: (text, state) => {
        options.onLevelState?.(text, state);
      },
    });
    const tracking = createQrTrackingController({
      frontEnd,
      solvePose: (input) => solveQrPose({ ...input, solver: pnpSolver }),
      fetchLevel: async (text) => {
        const level = await levelSource.fetchLevel(text);
        // The fused trackers need the printed size (plan §72 #1). Only a
        // level with geo votes, so a geo-less one (debug, trigger) is never
        // solved (plan §75 #2).
        fusedVotes.noteLevelSize(
          text,
          level.qr.geo ? level.qr.physicalSizeM : undefined
        );
        return level;
      },
      resolveStablePose: (text) => fusedVotes.resolveStablePose(text),
      // The source owns the retry timing; without this the controller's own
      // cache would keep the first failure for the whole session.
      shouldCacheLevel: (level) => levelSource.shouldCacheLevel(level),
      dispatchVotes: (votes) => {
        const text = lastVotedText;
        if (text === null) return;
        // ACCEPTANCE first, budget second (the shared contract's order):
        // `recordGpsEvent` silently no-ops until the session zero exists, so
        // charging the budget for dropped votes would spend it on nothing -
        // and `syntheticVotes` would over-report, permanently understating
        // `alignmentSampleCount` and risking a false decline at the
        // MIN_ALIGNMENT_SAMPLES gate (PR #385 review).
        if (storeRef.get().getState().gpsData?.zero == null) return;
        if (!voteBudget.tryConsume(text)) return;
        for (const vote of votes) {
          storeRef.get().dispatch(recordGpsEvent(vote));
          syntheticVotes += 1;
        }
      },
      // The RAW record rides the validated DECODE, not the solved pose:
      // decision D-A says a recording stays algorithm-agnostic whatever else
      // the session is doing, and a code whose level does not exist yet -
      // which is every code on an authoring walk - never reaches a lock.
      onDetection: (event) => {
        // The controller hands `dispatchVotes` no text, and `onDetection`
        // runs BEFORE the vote for the same lock, so this is the code the
        // next batch belongs to (same seam the TourViewer uses).
        lastVotedText = event.text;
      },
      onRawDetection: (raw) => {
        const projectionMatrix = getProjectionMatrix();
        if (projectionMatrix === null) return;
        storeRef.get().dispatch(
          recordQrDetection({
            text: raw.text,
            timestamp: raw.timestamp,
            corners: raw.corners,
            cameraPose: raw.cameraPose,
            projectionMatrix,
            imageWidth: raw.imageWidth,
            imageHeight: raw.imageHeight,
            // Kept so a replay can ignore native-order frames (plan §60).
            ...(raw.orderSource ? { orderSource: raw.orderSource } : {}),
          })
        );
        fusedVotes.onRecorded(raw.text);
      },
      getIntrinsics: (image) => {
        const projectionMatrix = getProjectionMatrix();
        return projectionMatrix === null
          ? null
          : intrinsicsFromProjection(
              projectionMatrix,
              image.width,
              image.height
            );
      },
      syntheticAccuracyM: VOTE_ACCURACY_M,
      voteBaselineM: VOTE_BASELINE_M,
      voteCount: VOTE_COUNT,
      minIntervalMs: 0,
    });
    setProducer(tracking);
    frameSink = tracking;
    startCameraFrameCapture({
      intervalMs: qr.intervalMs,
      captureSize: qr.captureSize,
      // Skip the GPU readback while a detect (incl. a level fetch) is in
      // flight - the scheduler would drop that frame anyway.
      wantsFrame: () => !tracking.isBusy(),
    });
    disposeLevelSource = () => {
      levelSource.dispose();
    };
    usingLevels = true;
  }

  const producer = usingLevels
    ? null
    : createQrDetectionController({
        detect,
        getProjectionMatrix,
        recordDetection: (observation) =>
          storeRef.get().dispatch(recordQrDetection(observation)),
        // The camera-frame source owns the cadence; detect every delivered frame.
        minIntervalMs: 0,
      });
  if (producer !== null) {
    setProducer(producer);
    frameSink = producer;
    // Begin delivering frames at the configured cadence + capture resolution.
    startCameraFrameCapture({
      intervalMs: qr.intervalMs,
      captureSize: qr.captureSize,
      wantsFrame: () => !producer.isBusy(),
    });
  }

  // --- Consumer / debug viz (WS-5) -----------------------------------------
  const debug = createQrDebugController({
    getState: () => storeRef.get().getState(),
    getArWorldGroup,
    // One deriver, two consumers: the debug cube and the session's sighting
    // fold. A second deriver would double the PnP work and could disagree
    // with what is drawn.
    onPlacement: (text, placement, timestampMs) => {
      sightings.onPlacement(text, placement, timestampMs);
    },
  });

  // Coalesce the per-action debug updates to at most one per animation frame
  // (F3): the recorder store bursts (depth + GPS + ~8 Hz QR detections), and
  // there is no value rendering more than once per frame. The deriver already
  // makes each update O(1)/detection (F1+F2); this just caps the frequency.
  // (Mirrors the rAF coalescing main.ts uses for the frame-tile/map overlays.)
  let rafId: number | null = null;
  const scheduleUpdate = (): void => {
    if (rafId !== null) return; // an update is already queued for this frame
    rafId = requestAnimationFrame(() => {
      rafId = null;
      debug.update();
      // A GPS fix is a store change: codes still waiting for a mature
      // alignment (D28 revised) follow it. After `debug.update()`, so a
      // placement derived in this frame is reported first.
      sightings.noteAlignment();
      options.onQrStateChanged?.();
    });
  };

  // Store-swap following via the shared helper (quality-review G-11). The
  // synchronous `debug.update()` inside attach reflects pre-existing markers
  // immediately on the initial wiring AND on every store swap (Start
  // Recording / replay) — the two paths used to duplicate the call.
  // The fold is DISCARDED on every store swap, and the first attach is not a
  // swap. The accumulator lives for the whole AR session, but the recorded
  // action stream only begins when Start Recording swaps the store: a sighting
  // folded while the user was lining up is real evidence the phone saw, and it
  // is not in the zip. Minting from it would write a position into
  // qr/<id>.json that replaying actions/ cannot reproduce - the thing decision
  // D-A exists to prevent - and would count visits the recording does not
  // contain. The swap also crosses an alignment-solve boundary, so the two
  // halves are not comparable evidence even in principle.
  let attached = false;
  const stopFollowing = followStore(storeRef, (store: RecorderStore) => {
    if (attached) {
      // The sightings AND the alignments kept for them.
      sightings.reset();
      syntheticVotes = 0;
      // The new store restarts its GPS list, so a code that spent its
      // budget against the previous one must be allowed to vote again.
      voteBudget.reset();
      fusedVotes.resetForStore();
    }
    attached = true;
    const detach = store.subscribe(scheduleUpdate);
    debug.update();
    return detach;
  });

  return () => {
    stopCameraFrameCapture();
    disposeLevelSource?.();
    // dispose, not reset: a decode or level fetch in flight must reach no
    // callback after the session ended (QR near-frontal pose plan §61).
    frameSink?.dispose();
    setProducer(null);
    options.setSightingFeeder?.(null);
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    stopFollowing();
    debug.dispose();
  };
}
